/**
 * Pure helpers for composing per-XPU `trackTransform` and root
 * `XtsConfig.orientation` into Three.js Matrix4 instances. Lives in geometry/
 * so the component layer (XtsViewer3D, XtsScene) and the test suite share a
 * single source of truth — no duplicated math.
 *
 * Composition order:
 *
 *     world = orientation ⊗ trackTransform ⊗ partTransformation ⊗ chain
 *
 * `partTransformation` is applied inside the per-XPU group by XtsScene, so
 * the helpers here only cover the outermost two layers.
 */

import { Euler, Matrix4, Quaternion, Vector3 } from 'three';
import type {
  Orientation,
  PartTransformation,
  TrackTransform,
} from '../types.js';

const DEG2RAD = Math.PI / 180;

/**
 * Default Z lift for a track that does not specify `positionMm`. The XTS
 * track sits on a base plate / table top in real installations, so 0 mm
 * (= floor) is rarely what the user wants. 200 mm matches the standard
 * Beckhoff table height used throughout the demo configs and renders
 * correctly under the default shadow-casting ground plane.
 */
export const DEFAULT_TRACK_Z_MM = 200;

/** Build the Matrix4 of the root orientation (defaults to identity). */
export function orientationMatrix(o: Orientation | undefined): Matrix4 {
  if (!o) return new Matrix4();
  const pos = o.positionMm ?? [0, 0, 0];
  const rot = o.rotationDegEuler ?? [0, 0, 0];
  return new Matrix4().compose(
    new Vector3(pos[0], pos[1], pos[2]),
    new Quaternion().setFromEuler(
      new Euler(rot[0] * DEG2RAD, rot[1] * DEG2RAD, rot[2] * DEG2RAD, 'XYZ'),
    ),
    new Vector3(1, 1, 1),
  );
}

/**
 * Build the Matrix4 for a per-XPU `trackTransform`. Uniform scale only —
 * non-uniform would distort path radii and break GLB lighting.
 *
 * When `positionMm` is omitted (or `tt` itself is null/undefined), the
 * track lifts to `DEFAULT_TRACK_Z_MM` in Z so it sits on a notional
 * table top rather than the floor.
 */
export function trackTransformMatrix(
  tt: TrackTransform | null | undefined,
): Matrix4 {
  if (!tt) return new Matrix4().makeTranslation(0, 0, DEFAULT_TRACK_Z_MM);
  const pos = tt.positionMm ?? [0, 0, DEFAULT_TRACK_Z_MM];
  const rot = tt.rotationDegEuler ?? [0, 0, 0];
  const s = tt.scaleUniform ?? 1;
  return new Matrix4().compose(
    new Vector3(pos[0], pos[1], pos[2]),
    new Quaternion().setFromEuler(
      new Euler(rot[0] * DEG2RAD, rot[1] * DEG2RAD, rot[2] * DEG2RAD, 'XYZ'),
    ),
    new Vector3(s, s, s),
  );
}

/**
 * Build the Matrix4 for a `PartTransformation`. Returns identity when
 * `pt` is null/undefined — parts without an explicit transform sit in
 * their XPU's track-transform frame.
 */
export function partTransformationMatrix(
  pt: PartTransformation | null | undefined,
): Matrix4 {
  if (!pt) return new Matrix4();
  const pos = pt.offsetMm ?? [0, 0, 0];
  const rot = pt.rotationDegEuler ?? [0, 0, 0];
  return new Matrix4().compose(
    new Vector3(pos[0], pos[1], pos[2]),
    new Quaternion().setFromEuler(
      new Euler(rot[0] * DEG2RAD, rot[1] * DEG2RAD, rot[2] * DEG2RAD, 'XYZ'),
    ),
    new Vector3(1, 1, 1),
  );
}

/**
 * Compose orientation ⊗ trackTransform — returns the world-frame matrix
 * applied to the entire XPU subtree (modules + movers + tools + stations +
 * info bars + mover-bound custom assets).
 */
export function composeWorldMatrix(
  orientation: Orientation | undefined,
  trackTransform: TrackTransform | null | undefined,
): Matrix4 {
  return new Matrix4()
    .copy(orientationMatrix(orientation))
    .multiply(trackTransformMatrix(trackTransform));
}

/**
 * Compose orientation ⊗ trackTransform ⊗ partTransformation — the full
 * world-frame matrix applied to a point sampled in the part's chain frame.
 * Equivalent to `composeWorldMatrix` followed by the part transform.
 */
export function composePartWorldMatrix(
  orientation: Orientation | undefined,
  trackTransform: TrackTransform | null | undefined,
  partTransformation: PartTransformation | null | undefined,
): Matrix4 {
  return new Matrix4()
    .copy(composeWorldMatrix(orientation, trackTransform))
    .multiply(partTransformationMatrix(partTransformation));
}
